import { MouseEvent, ReactNode, useState } from "react";
import { observer } from "mobx-react-lite";
import { AddIcon, MinusIcon } from "@chakra-ui/icons";
import { Box, Center, Flex, FlexProps, Spacer, Text } from "@chakra-ui/react";

import {
  mean,
  Lang,
  Dictionary,
  isUndefined,
  isClose,
  ClipBoard as NoliClipBoard,
} from "@noli/core";
import {
  langStore,
  MaskResponse,
  MASK_PRESET_TYPES,
  translate,
  useImageCrop,
  useMask,
  useRenderType,
  useSafeExecute,
  useSelecting,
} from "@noli/business";

import "./BasicEditor.scss";
import "./ImageEditor.scss";
import { getOriginMeta, MetaType, VariationType } from "@/types/meta";
import { ENV } from "@/utils/constants";
import { genBlock } from "@/utils/bem";
import { checkAIMeta, checkMeta, timestampToDate, toast, useOwnToast } from "@/utils/misc";
import { themeStore } from "@/stores/theme";
import { ReactComponent as CopyIcon } from "@/assets/icons/copy.svg";
import { ReactComponent as ArrangeIcon } from "@/assets/icons/arrange.svg";
import { ReactComponent as TriangleIcon } from "@/assets/icons/triangle.svg";
import { ReactComponent as CropImageIcon } from "@/assets/icons/crop_image.svg";
import { ReactComponent as CornerRadiusIcon } from "@/assets/icons/corner-radius.svg";
import Icon from "@/components/Icon";
import Select from "@/components/Select";
import IconButton from "@/components/IconButton";
import { SubPanelTitleWithIcon, SubPanelTitleWithSubTitle } from "@/components/SubPanel";
import SubEditorPanel from "@/components/SubPanel/SubEditor";
import Property, { IProperty } from "@/components/Property";
import { FiltersPanel } from "./common";
import { Editor_Words } from "@/utils/lang/editor";
import { ImageBusinessOp } from "../components/ImageBusinessOp";
import { AIParamsMeta, parseMetaToParams } from "../stores/aiParams";
import { updateUI } from "@/stores/ui";
import { AI_PARAMS_TYPES } from "../constant";
import { thirdPartyParamsOrder } from "@/thirdParty/paramsDisplay";
import { THIRD_PARTY_MODEL_TYPES } from "@/thirdParty/constants";
import { isAuthUser } from "@/utils/status";

function translateRenderType(renderType: string, lang: Lang): string {
  return `${translate(`${renderType}-renderType`, lang)} (${renderType})`;
}
const ToolbarPanel = observer(() => {
  const lang = langStore.tgt;
  const { loginBtnColor } = themeStore.styles;

  const t = useOwnToast();
  const info = useSelecting("basic")({ fixed: 0 });
  const { renderType, setRenderType } = useRenderType();
  const { cropBegin } = useImageCrop();

  if (!info) return null;
  const { imgWH, displayNode } = info;
  if (displayNode?.type !== "image") return null;

  const onSizeReset = () => {
    if (!imgWH) return;
    const { w, h } = displayNode.wh;
    const { w: iw, h: ih } = imgWH;
    if (isClose(w, iw) && isClose(h, ih)) {
      toast(t, "info", translate(Editor_Words["size-reset-no-need-message"], lang));
      return;
    }
    useSafeExecute("editNodeBase", displayNode, true)({ w: iw, h: ih });
  };

  return (
    <SubEditorPanel
      h="100px"
      panelTitle={
        <SubPanelTitleWithSubTitle
          mb="8px"
          title={translate(Editor_Words["image-render-type"], lang)}
          subTitle={
            <Text
              pr="16px"
              color={loginBtnColor}
              cursor="pointer"
              fontSize="12px"
              userSelect="none"
              onClick={onSizeReset}
              as="button"
              disabled={!imgWH}
              _disabled={{ color: "gray", cursor: "not-allowed" }}
            >
              {translate(Editor_Words["size-reset"], lang)}
            </Text>
          }
          align="center"
        />
      }
    >
      <Flex w="100%" h="32px" px="16px">
        <Select
          w="100%"
          h="100%"
          mt="6px"
          pl="12px"
          value={translateRenderType(renderType ?? "fit", lang)}
          options={["fill", "cover", "fit"]}
          iconProps={{ pr: "8px" }}
          optionConverter={(option: string) => translateRenderType(option, lang)}
          onOptionClick={setRenderType({ trace: true })}
        />
        {/* <Box w="32px" h="32px" ml="12px" overflow="hidden" borderRadius="8px">
          <IconButton strokeByCurrentColor icon={CropImageIcon} onClick={cropBegin} />
        </Box> */}
      </Flex>
    </SubEditorPanel>
  );
});
function MaskProperty(props: IProperty<number>) {
  return <Property w="100%" h="32px" px="12px" inputPadding="30%" {...props} />;
}

const block = genBlock("c-basic-editor");

function MaskEditor({
  displayNode,
  lang,
  maskType,
  maskParams,
  updateMask,
}: { lang: Lang } & MaskResponse) {
  if (maskType && maskParams) {
    switch (maskType) {
      case "rectangle": {
        const { radius } = maskParams;
        const values = Object.values(radius);
        const updateRectangleMask = updateMask({ trace: true })("rectangle");
        return (
          <MaskProperty
            identifier={`${displayNode.alias}-rectangle-mask`}
            icon={
              <Icon svg={CornerRadiusIcon} className={block({ e: "icon" })} fillbyCurrentColor />
            }
            value={values.length === 0 ? 0 : mean(values)}
            setValue={(v) => updateRectangleMask({ radius: { lt: v, rt: v, rb: v, lb: v } })}
          />
        );
      }
      case "ellipse": {
        const { w, h } = maskParams;
        const updateEllipseMask = updateMask({ trace: true })("ellipse");
        return (
          <Box>
            <MaskProperty
              identifier={`${displayNode.alias}-ellipse-mask-W`}
              text="W"
              value={w}
              setValue={(v) => updateEllipseMask({ w: v, h })}
            />
            <MaskProperty
              identifier={`${displayNode.alias}-ellipse-mask-H`}
              text="H"
              value={h}
              setValue={(v) => updateEllipseMask({ w, h: v })}
            />
          </Box>
        );
      }
      case "star": {
        const { radius, ratio, counts } = maskParams;
        const updateStarMask = updateMask({ trace: true })("star");
        return (
          <Box>
            <MaskProperty
              identifier={`${displayNode.alias}-star-mask-radius`}
              icon={
                <Icon svg={CornerRadiusIcon} className={block({ e: "icon" })} fillbyCurrentColor />
              }
              value={radius}
              setValue={(v) => updateStarMask({ radius: v, ratio, counts })}
            />
            <MaskProperty
              identifier={`${displayNode.alias}-star-mask-ratio`}
              text={translate("star-mask-ratio", lang)}
              value={ratio}
              setValue={(v) => updateStarMask({ radius, ratio: v, counts })}
            />
            <MaskProperty
              identifier={`${displayNode.alias}-star-mask-counts`}
              text={translate("star-mask-counts", lang)}
              value={counts}
              setValue={(v) => updateStarMask({ radius, ratio, counts: v })}
            />
          </Box>
        );
      }
    }
  }
  return (
    <Text px="16px" py="4px" color="#939393" fontSize="12px" userSelect="none">
      {translate("no-mask-message", lang)}
    </Text>
  );
}
const MaskPanel = observer(() => {
  const lang = langStore.tgt;
  const maskResponse = useMask();
  if (!maskResponse) {
    return null;
  }
  const { maskType, setMask, deleteMask } = maskResponse;
  const title = `${maskType ? translate(maskType, lang) : ""} ${translate("image-mask", lang)}`;
  return (
    <SubEditorPanel
      h="174px"
      panelTitle={
        <SubPanelTitleWithIcon
          h="32px"
          mb="8px"
          title={title}
          customIcons={[
            <Select
              key="addMask"
              w="32px"
              h="32px"
              icon={<IconButton icon={AddIcon} />}
              iconProps={{ w: "30px" }}
              options={MASK_PRESET_TYPES}
              optionConverter={(type) => translate(type, lang)}
              onOptionClick={(type) => setMask({ trace: true })(type)()}
            />,
            <IconButton
              key="deleteMask"
              mr="8px"
              icon={MinusIcon}
              onClick={deleteMask({ trace: true })}
            />,
          ]}
        />
      }
    >
      <MaskEditor lang={lang} {...maskResponse} />
    </SubEditorPanel>
  );
});

const imageBlock = genBlock("c-image-editor");
const paramsOrder = [
  "type",
  "w",
  "h",
  "text",
  "negative_prompt",
  "highres_info",
  "url",
  "originUrl",
  "fidelity",
  "custom_embeddings",
  "seed",
  "model",
  "version",
  "sampler",
  "use_circular",
  "is_anime",
  "num_steps",
  "guidance_scale",
  "variations",
  "wh",
  "isDrag",
  "max_wh",
  "mask_url",
  "use_refine",
  "refine_fidelity",
  "timestamp",
  // debug fields
  "debug.from",
  "debug.isImport",
  "debug.localModels",
  "debug.prompt_lang",
  "debug.prompt_original",
  "debug.prompt_originalNegative",
  "debug.prompt_style",
  "debug.prompt_artist",
  "debug.prompt_cat",
];

const paramsMapping: Dictionary<string> = {
  text: "prompt",
  url: "original_image",
  originUrl: "original_image",
};
Object.entries(thirdPartyParamsOrder).forEach(([k, vs]) =>
  vs.forEach((v) => {
    const join = `${k}.${v}`;
    paramsOrder.splice(1, 0, join);
    paramsMapping[join] = v;
  }),
);

function getStringValue(key: string, meta: MetaType): string | undefined {
  // debug fields
  if (key.startsWith("debug.") && ENV !== "production") {
    if (key === "debug.isImport") {
      return `${meta.data.isImport ?? false}`;
    }
    if (key === "debug.localModels") {
      return `${JSON.stringify(meta.data.localModels ?? {})}`;
    }
    if (
      key === "debug.from" &&
      (!checkAIMeta(meta) || meta.type === "img2img" || meta.type === "img2img.variation")
    ) {
      return JSON.stringify(meta.data.from ?? "none");
    }
    if (key.startsWith("debug.prompt") && checkAIMeta(meta)) {
      const promptInfo = meta.data.promptInfo;
      if (!promptInfo) return;
      return (promptInfo as any)[key.split("_")[1]];
    }
    return;
  }
  // third party fields
  for (const model of THIRD_PARTY_MODEL_TYPES) {
    if (key.startsWith(model)) {
      const split = key.split(".");
      return (meta.data as any)[split.slice(1, split.length).join(".")];
    }
  }
  // normal fields
  const value = (meta.data as any)[key];
  if (isUndefined(value)) return;
  if (key === "wh") {
    return JSON.stringify(value);
  }
  if (key === "variations") {
    return (value as VariationType[]).map((v) => `${v.seed} (${v.strength.toFixed(2)})`).join(", ");
  }
  if (key === "timestamp") {
    return timestampToDate(value);
  }
  if (key === "custom_embeddings") {
    console.log("value: ", value);
    return value.join(", ");
  }

  if (typeof value === "object" && !Array.isArray(value)) {
    return JSON.stringify(value);
  }
  return `${value}`;
}

function formatOriginMetaString(meta: MetaType): ReactNode | undefined {
  const { type, data } = meta;

  const valueNodes = Object.entries(data).map(([key, value]) => {
    console.log("key", key, "value: ", value);
    return value ? (
      <Box key={key} px="10%" py="4px">
        <Text mr="6px" color="text.caption" fontWeight={600} as="span">
          origin_{key}:
        </Text>
        <Text color="text.input" as="span">
          {typeof value === "object" ? JSON.stringify(value) : value}
        </Text>
      </Box>
    ) : null;
  });

  return (
    <>
      <Box px="10%" py="4px">
        <Text mr="6px" color="text.caption" fontWeight={600} as="span">
          origin_type:
        </Text>
        <Text color="text.input" as="span">
          {type}
        </Text>
      </Box>
      {valueNodes}
    </>
  );
}
export const ParamsPanel = observer<FlexProps>((props) => {
  const [expand, setExpand] = useState(false);
  const t = useOwnToast();
  const lang = langStore.tgt;
  const displayNode = useSelecting("raw").displayNode;
  const { panelBg, separatorColor, inputColor, captionColor } = themeStore.styles;

  if (displayNode?.type !== "image") return null;
  const meta = displayNode.meta;
  const type = meta.type;
  if (!checkMeta(meta)) return null;

  async function handleCopyParams(e: MouseEvent): Promise<void> {
    e.stopPropagation();
    if (!checkMeta(meta)) return;
    NoliClipBoard.copyPacks.length = 0;
    NoliClipBoard.cutPacks.length = 0;
    await navigator.clipboard.writeText(JSON.stringify(meta));
    toast(t, "success", translate(Editor_Words["copy-parameters-success-message"], lang));
  }

  function handleArrangeParams(e: MouseEvent) {
    e.stopPropagation();
    parseMetaToParams(meta as AIParamsMeta);
    updateUI("leftPanel", "params2img");
  }

  const renderParams = (meta: MetaType, origin?: boolean): ReactNode | undefined => {
    return paramsOrder.map((key) => {
      let caption, stringValue;
      if (meta.type === "duplicate") {
        const originMeta = meta.data.from;

        if (key === "debug.from" && originMeta) {
          const nodes = renderParams(originMeta, true);
          return (
            <Box key={key} px="10%" py="4px">
              <Text color={captionColor} fontWeight={600}>
                origin:{" "}
              </Text>
              <Box>{nodes}</Box>
            </Box>
          );
        }
      }
      if (meta.type === "SOD") {
        if (key === "url") {
          return null;
        }
      }
      if (key === "type") {
        caption = "type";
        stringValue = meta.type;
      } else {
        stringValue = getStringValue(key, meta);
        if (isUndefined(stringValue)) return null;
        caption = paramsMapping[key] ?? key;
      }

      return (
        <Box key={key} px={origin ? "4%" : "10%"} py="4px">
          <Text mr="6px" color={captionColor} fontWeight={600} as="span">
            {caption}:
          </Text>
          <Text color={inputColor} as="span">
            {stringValue}
          </Text>
        </Box>
      );
    });
  };

  return (
    <Flex
      borderTop="1px"
      borderColor={separatorColor}
      overflow="hidden"
      direction="column"
      userSelect="text"
      pb="24px"
      {...props}
    >
      <Center>
        <Flex
          w="85%"
          h="24px"
          mt="16px"
          mb="8px"
          bg={panelBg}
          color={inputColor}
          align="center"
          position="relative"
          zIndex={2}
          fontSize="14px"
          cursor="pointer"
          textAlign="center"
          onClick={() => setExpand(!expand)}
        >
          <Icon
            squared={false}
            className={imageBlock({ m: expand ? "expanded" : "folded" })}
            svg={TriangleIcon}
          />
          <Text>{`${checkAIMeta(meta) ? "AI " : ""}${translate(
            Editor_Words["ai-parameters-appendix"],
            lang,
          )}`}</Text>
          <Spacer />
          {AI_PARAMS_TYPES.includes(type) ? (
            <Flex align="center" onClick={handleArrangeParams} mr="10px">
              <Icon squared={false} className={imageBlock({ m: "icon" })} svg={ArrangeIcon} />
              <Text ml="2px">{translate(Editor_Words.arrangeParameters, lang)}</Text>
            </Flex>
          ) : null}
          <Flex align="center" onClick={handleCopyParams}>
            <Icon squared={false} className={imageBlock({ m: "icon" })} svg={CopyIcon} />
            <Text>{translate(Editor_Words["copy-parameters"], lang)}</Text>
          </Flex>
        </Flex>
      </Center>
      <Flex
        w="100%"
        h={expand ? "100%" : "0px"}
        overflow="hidden"
        flexDir="column"
        flexShrink={1}
        fontSize="14px"
        transition="height 0.3s cubic-bezier(.08,.52,.52,1)"
      >
        {/* {paramsOrder.map((key) => {
          let caption, stringValue;
          if (meta.type === "duplicate") {
            const originMeta = getOriginMeta(meta);

            if (key === "debug.from" && originMeta) {
              stringValue = formatOriginMetaString(originMeta);
              return stringValue;
            }
          }
          if (meta.type === "SOD") {
            if (key === "url") {
              return null;
            }
          }
          if (key === "type") {
            caption = "type";
            stringValue = meta.type;
          } else {
            stringValue = getStringValue(key, meta);
            if (isUndefined(stringValue)) return null;
            caption = paramsMapping[key] ?? key;
          }

          return (
            <Box key={key} px="10%" py="4px">
              <Text mr="6px" color={captionColor} fontWeight={600} as="span">
                {caption}:
              </Text>
              <Text color={inputColor} as="span">
                {stringValue}
              </Text>
            </Box>
          );
        })} */}
        {renderParams(meta)}
      </Flex>
    </Flex>
  );
});

const ImageEditor: React.FC<FlexProps> = (props) => {
  const { separatorColor } = themeStore.styles;
  const node = useSelecting("raw").displayNode;

  return node?.type === "image" ? (
    <Flex className={imageBlock()} w="100%" h="100%" direction="column" {...props}>
      <ToolbarPanel />
      {/* <MaskPanel /> */}
      {/* <FiltersPanel flex={1} /> */}
      {isAuthUser() ? (
        <Center
          borderTop="1px"
          borderColor={separatorColor}
          w="100%"
          padding="24px 24px"
          flexDirection="column"
        >
          <ImageBusinessOp />
        </Center>
      ) : null}
      <ParamsPanel flex={1} />
    </Flex>
  ) : null;
};

export default observer(ImageEditor);
